import React from 'react';
import { compose } from 'recompose';
import { inject, observer } from 'mobx-react';
import { withSnackbar } from 'notistack';
import DataGrid from '@dataGrid';
import StatusBar from '@dataGrid/components/StatusBar';
import GridConfig from './GridConfig';
import { withUserProfile } from '../../../../../userProfile/userProfileContext';
import { RMAXLogger } from '@RMAXLogger';
import { getDataWithQueryString } from '../../../../../api/componentAPIs';
import Events from '../../../../../utilities/EventEmitter';
import DispatchPanel from '../DispatchPanel/index';
import TrailerContents from '../TrailerContents/index';
import appConstants from '../../../../../appConstants';
import { getPreSelectedTrailer } from '../DispatchPanel/preDispatchTrailerUtils';
import { getDateWithUtcHoursAndMinFromTimeString } from '@dataGrid/gridUtilities';
import dispatchPanelConstants from '../DispatchPanel/constants';
import { getSpecialNeedsOptionsWithServiceLevel } from '../../../utils';
@inject('DispatchStore')
@observer
class ClosedTrailers extends React.Component {
    constructor(props) {
        super(props);
        this.gridConfig = new GridConfig({
            userProfile: this.props.userData.userProfile
        });
        this.gridApi = {};
        this.state = {
            closedTrailersList: [],
            searchText: '',
            showDispatchPanel: false,
            showTrailerContents: false,
            selectedTrailer: null,
            gridStatus: appConstants.GRID_STATUS.LOADING
        };
        this.selectedTrailer = null;
        this.gridHeaderId = `gridHeader_${this.props.glContainer._config.id}`;
    }

    componentDidMount() {
        this.loadClosedTrailers();
        this.props.glEventHub.on(this.gridHeaderId, this.onSearchTextChanged);
        Events.on('ClosedTrailersUpdated', this.updateClosedTrailers);
        Events.on('ClosedTrailerStatusUpdated', this.updateClosedTrailerStatus);
        Events.on('UpdatedClosedTrailers', this.updateClosedTrailerList);
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.userData.userProfile.terminal.code !== this.props.userData.userProfile.terminal.code
        ) {
            this.loadClosedTrailers();
        }
    }
    componentWillUnmount() {
        // Clean up grid API reference
        this.gridApi = null;
        this.props.glEventHub.off(this.gridHeaderId, this.onSearchTextChanged);
        Events.off('ClosedTrailersUpdated', this.updateClosedTrailers);
        Events.off('ClosedTrailerStatusUpdated', this.updateClosedTrailerStatus);
        Events.off('UpdatedClosedTrailers', this.updateClosedTrailerList);
    }

    onSearchTextChanged = (searchText) => {
        this.setState({
            searchText
        });
    };

    // SpecialNeedsRenderer's COD tooltip reads isCodCharge/isFreightCharge (not isCodShipment, which only
    // decides whether the icon shows) — derive them locally since GetClosedTrailers doesn't send them.
    mapCodFields = (trailer) => ({
        ...trailer,
        isCodCharge: trailer.isCodShipment,
        isFreightCharge: trailer.isFreightShipment,
    });

    loadClosedTrailers = () => {
        const terminalCode = this.props.userData.userProfile.terminal.code;
        getDataWithQueryString('GetClosedTrailers', `terminalCode=${terminalCode}`)
            .then((response) => {
                if (response.status >= 200 && response.status <= 400) {
                    this.setState({
                        closedTrailersList: (response.data || []).map(this.mapCodFields),
                        gridStatus: appConstants.GRID_STATUS.SUCCESS
                    });
                } else {
                    this.setState({
                        closedTrailersList: [],
                        gridStatus: appConstants.GRID_STATUS.ERROR
                    });
                }
            })
            .catch((error) => {
                RMAXLogger.error('Closed_Trailers_Load', {
                    message: error?.message,
                    status: error?.response?.status,
                    terminalCode
                });
                this.setState({
                    closedTrailersList: [],
                    gridStatus: appConstants.GRID_STATUS.ERROR
                });
            });
    };

    updateClosedTrailerList = (data, refreshDriver = true) => {
        if (refreshDriver) {
            Events.emit('refreshAvailableDrivers');
        }
        this.loadClosedTrailers();
        if (data.success) {
            if (data.driverId.toUpperCase() === this.state.selectedDriverId.toUpperCase()) {
                this.props.enqueueSnackbar(`Driver is assigned to trailer successfully`, {
                    variant: 'success'
                });
                this.setState({
                    selectedDriverId: null
                });
            }
        }
    };

    updateClosedTrailers = (data) => {
        if (!data?.success || !data?.trailerId) {
            return;
        }
        this.setState((prevState) => {
            const existingIndex = prevState.closedTrailersList.findIndex(
                (trailer) =>
                    trailer.trailerId?.toUpperCase() === data.trailerId.toUpperCase()
            );
            if (existingIndex === -1) {
                return {
                    closedTrailersList: [...prevState.closedTrailersList, data]
                };
            }
            const closedTrailersList = [...prevState.closedTrailersList];
            closedTrailersList[existingIndex] = {
                ...closedTrailersList[existingIndex],
                ...data
            };
            return { closedTrailersList };
        });
    };
    getClosedTrailerStatus = () => {
        const trailerStatusConfig =
            this.props.DispatchStore.staticClientData?.clientCodes?.trailerStatusConfig ?? [];
        return (
            trailerStatusConfig.find(
                (config) => config.rmaxCode === dispatchPanelConstants.RMAX_CLOSED_TRAILER_CODE
            )?.status ?? null
        );
    };

    // TrailerUpdated fires for any inventory change. Reload Closed Trailers only when the new
    // status matches the client closed status from trailerStatusConfig (rmaxCode CLO).
    updateClosedTrailerStatus = (data) => {
        if (!data?.success || !data?.trailerId) {
            return;
        }
        const closedStatus = this.getClosedTrailerStatus();
        if (
            closedStatus &&
            data.trailerStatus?.toUpperCase() === closedStatus.toUpperCase()
        ) {
            this.loadClosedTrailers();
        }
    };

    onGridReady = ({ api }) => {
        this.gridApi = api;
        this.gridApi.runCommand = this.runCommand;
    };

    runCommand = (command, data) => {
        if (command === 'PreDispatch') {
            this.openDispatchPanel(data.trailer);
        }
    };

    constructStatusBar = (filteredData) => {
        const totalCount = filteredData.length;
        return [
            {
                label: `${totalCount} Trailers`,
                filterCondition: ''
            }
        ];
    };

    openDispatchPanel = (data) => {
        if (data) {
            this.selectedTrailer = [getPreSelectedTrailer({ route: null, trailer: { ...data, plannedRouteStartTime: getDateWithUtcHoursAndMinFromTimeString(data.plannedRouteStartTime) }, isAlreadyDispatchedRoute: false})];
            this.setState({
                showDispatchPanel: true
            });
        }
    };

    closeDispatchPanel = () => {
        this.setState({
            showDispatchPanel: false
        });
        this.selectedTrailer = null;
    };

    onCellDoubleClicked = (params) => {
        this.openTrailerContents(params.data);
    };

    openTrailerContents = (data) => {
        this.setState({
            showTrailerContents: true,
            selectedTrailer: data
        });
    };

    closeTrailerContents = () => {
        this.setState({
            showTrailerContents: false,
            selectedTrailer: null
        });
    };

    render() {
        const { closedTrailersList } = this.state;
        const priorityShipmentList = this.props.DispatchStore.priorityAllShipmentList;
        const specialNeedsOptions = getSpecialNeedsOptionsWithServiceLevel(priorityShipmentList);
        return (
            <>
                <DataGrid
                    defaultColDef={this.gridConfig.config.defaultColDef}
                    columnDefs={this.gridConfig.config.columns}
                    config={this.gridConfig.config}
                    rowSelection={this.gridConfig.config.rowSelection}
                    rowDeselection={this.gridConfig.config.rowDeselection}
                    rowData={closedTrailersList}
                    onGridReady={this.onGridReady}
                    panelLabel={(filteredData) => (
                        <StatusBar data={this.constructStatusBar(filteredData)} />
                    )}
                    layoutContainer={this.props.glContainer}
                    getRowId={(params) => params.data.trailerId}
                    quickFilterText={this.state.searchText}
                    onCellDoubleClicked={this.onCellDoubleClicked}
                    showErrorMessage={this.state.gridStatus}
                    context={{ specialNeedsOptions }}
                />

                {this.state.showDispatchPanel && (
                    <DispatchPanel
                        open={true}
                        onClose={this.closeDispatchPanel}
                        terminalCode={this.props.userData.userProfile.terminal.code}
                        terminalOffset={this.props.userData.userProfile.terminal.offset}
                        userProfile={this.props.userData.userProfile}
                        isPreDispatch={true}
                        selectedTrailer={this.selectedTrailer}
                        glEventHub={this.props.glEventHub}
                        allAvailableDriverList={this.props.DispatchStore.dispatchableDrivers}
                        dispatchedDrivers={this.props.DispatchStore.dispatchedDrivers.map(
                          (driver) => driver.driverId
                        )}
                    />
                )}
                {this.state.showTrailerContents && (
                    <TrailerContents
                        open={true}
                        handleClose={this.closeTrailerContents}
                        title="Trailer Information"
                        trailer={this.state.selectedTrailer}
                        terminalCode={this.props.userData.userProfile.terminal.code}
                        priorityShipmentList={priorityShipmentList}
                    />
                )}
            </>
        );
    }
}

export default compose(
    withUserProfile,
    withSnackbar
)(ClosedTrailers);
